import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FaCalendar, FaClock, FaUser, FaPhone, FaVideo, FaHome, FaStethoscope, FaHistory, FaMale, FaFemale, FaEye } from 'react-icons/fa';
import { API_BASE_URL, AUTH_TOKEN_KEY } from '../../utils/constants';
import { formatDate } from '../../utils/utils';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import DashboardLayout from '../../components/layout/DashboardLayout';
import './PastAppointmentsScreen.css';

const PastAppointmentsScreen = () => {
  const navigate = useNavigate();
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    fetchPastAppointments();
  }, []);

  const fetchPastAppointments = async () => {
    try {
      setLoading(true);
      const accessToken = localStorage.getItem(AUTH_TOKEN_KEY);
      
      if (!accessToken) {
        toast.error('Access token not found. Please log in again.');
        return;
      }

      const response = await fetch(`${API_BASE_URL}api/doctor/appointments?status=past`, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch past appointments');
      }

      const data = await response.json();
      setAppointments(data.appointments || []);
    } catch (error) {
      console.error('Error fetching past appointments:', error);
      toast.error('Failed to fetch past appointments');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchPastAppointments();
    setRefreshing(false);
  };

  const getServiceIcon = (serviceType) => {
    switch (serviceType) {
      case 'Tele Consultation':
      case 'teleConsultation':
        return <FaVideo className="service-icon-appointment" />;
      case 'inClinic':
        return <FaStethoscope className="service-icon-appointment" />;
      case 'homeVisit':
        return <FaHome className="service-icon-appointment" />;
      default:
        return <FaStethoscope className="service-icon-appointment" />;
    }
  };

  const getGenderIcon = (gender) => {
    if (gender === 'Male') {
      return <FaMale className="gender-icon male" />;
    } else if (gender === 'Female') {
      return <FaFemale className="gender-icon female" />;
    }
    return <FaUser className="gender-icon" />;
  };

  if (loading) {
    return <LoadingSpinner message="Loading past appointments..." />;
  }

  return (
    <DashboardLayout title="Past Appointments" showBackButton={true}>
      <div className="past-appointments-header">
        <button 
          className="refresh-btn" 
          onClick={handleRefresh}
          disabled={refreshing}
        >
          <FaHistory className={refreshing ? 'spinning' : ''} />
          Refresh
        </button>
      </div>

      {appointments.length === 0 ? (
        <div className="empty-state">
          <FaCalendar className="empty-icon" />
          <h3>No past appointments</h3>
          <p>You don't have any completed appointments yet.</p>
        </div>
      ) : (
        <div className="appointments-list">
          {appointments.map((appointment) => {
            const borderColor = appointment.isCancelled ? '#ff6b6b' : '#636e72';
            
            return (
              <div key={appointment._id} className="appointment-card past" style={{ borderLeftColor: borderColor }}>
                <div className="patient-avatar">
                  {getGenderIcon(appointment.patientInfo.gender)}
                </div>
                
                <div className="patient-name">
                  {appointment.patientInfo.name}
                </div>
                
                <div className="appointment-details">
                  <div className="detail-item">
                    <FaCalendar className="detail-icon" />
                    <span>{new Date(appointment.selectedDate).toLocaleDateString()}</span>
                  </div>
                  
                  <div className="detail-item">
                    <FaClock className="detail-icon" />
                    <span>{appointment.selectedTimeSlot}</span>
                  </div>
                  
                  <div className="detail-item">
                    {getServiceIcon(appointment.serviceType)}
                    <span>{appointment.serviceType}</span>
                  </div>
                </div>
                
                <div className="appointment-footer">
                  <div className="fee">₹{appointment.fees}</div>
                  <div className={`status-badge ${appointment.isCancelled ? 'cancelled' : 'completed'}`}>
                    {appointment.isCancelled ? 'Cancelled' : 'Completed'}
                  </div>
                </div>
                
                <button 
                  className="view-details-btn"
                  onClick={() => navigate(`/appointment/${appointment._id}`)}
                >
                  <FaEye />
                  View Details
                </button>
              </div>
            );
          })}
        </div>
      )}
    </DashboardLayout>
  );
};

export default PastAppointmentsScreen;